<template>
  <!-- 문헌 수집 — 논문 레지스트리 목록 (Thermax LitRegistryPage 대응) -->
  <div class="lit-page">
    <div class="title_cont">
      <h2 class="title_txt">문헌 수집</h2>
    </div>
    <div class="ptc_container">
      <p class="lit-intro">
        각 연구팀이 수집하기로 한 논문을 DOI 로 등록해 서로 겹치지 않게 하고, 추출한 데이터와 원문을 논문 단위로 모읍니다.
        등록·업로드는 <router-link to="/account/mypage/lit-token">문헌 업로드 토큰</router-link>을 발급받아 팀 PC 스크립트로 합니다.
        <span class="lit-pkg">팀 패키지:
          <a :href="pkgBase + procedureFile" download>수행 절차</a> ·
          <a :href="pkgBase + apiFile" download>API 사용법</a> ·
          <a :href="pkgBase + 'lit_register.py'" download>lit_register.py</a> ·
          <a :href="pkgBase + 'lit_push.py'" download>lit_push.py</a>
        </span>
      </p>

      <!-- 현황 칩 -->
      <div class="lit-chips" v-if="data">
        <button type="button" class="lit-chip" :class="{ on: !filter.group }" @click="selectGroup('')">
          전체 <b>{{ data.overall.toLocaleString() }}</b> 편
        </button>
        <button v-for="g in data.by_group" :key="g.key" type="button" class="lit-chip"
                :class="{ on: filter.group === g.key, muted: g.aggregate_excluded }"
                :title="g.aggregate_excluded ? '계열 집계에서 분리' : ''" @click="selectGroup(g.key)">
          {{ g.label }} <b>{{ g.count.toLocaleString() }}</b>
          <small v-if="!g.aggregate_excluded && aggregateTotal">({{ pct(g.count) }}%)</small>
        </button>
      </div>
      <div class="lit-chips lit-chips--sub" v-if="data">
        <span class="lit-chip-label">상태</span>
        <button v-for="s in data.by_status" :key="s.key" type="button" class="lit-chip small"
                :class="{ on: filter.status === s.key }" @click="toggleStatus(s.key)">
          {{ s.label }} <b>{{ s.count.toLocaleString() }}</b>
        </button>
        <span class="lit-chip-label">학교</span>
        <button v-for="i in data.by_institution" :key="i.key" type="button" class="lit-chip small"
                :class="{ on: filter.institution === i.key }" title="이 학교만 보기" @click="toggleInstitution(i.key)">
          {{ i.label }} <b>{{ i.count.toLocaleString() }}</b>
        </button>
      </div>

      <!-- 검색·필터 -->
      <div class="lit-toolbar">
        <div class="lit-search">
          <input type="text" v-model="search" placeholder="DOI · 제목 · 올린 사람 검색" @keyup.enter="applySearch">
          <button type="button" class="sm_btn" @click="applySearch">검색</button>
        </div>
        <label class="lit-check"><input type="checkbox" v-model="filter.mine" @change="reload(1)"> 내가 올린 것만</label>
        <div class="lit-toolbar-right">
          <router-link to="/literature/explore" class="sm_btn lit-link-btn">데이터 탐색 (검색·시각화)</router-link>
          <button type="button" class="sm_btn" :disabled="exporting || !data || data.total === 0" @click="exportXlsx">
            {{ exporting ? '내보내는 중…' : '엑셀 내보내기' }}
          </button>
        </div>
      </div>

      <!-- 표 -->
      <div class="lit-table-wrap" v-if="data && data.items.length">
        <table class="lit-table">
          <colgroup>
            <col style="width:22%"><col><col style="width:12%"><col style="width:8%"><col style="width:9%"><col style="width:11%"><col style="width:9%"><col style="width:6%"><col style="width:6%">
          </colgroup>
          <thead>
          <tr>
            <th>DOI</th><th>제목</th><th>분야</th><th>상태</th><th>올린 사람</th><th>학교</th><th>등록일</th><th>추출</th><th>원문</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="p in data.items" :key="p.id">
            <td class="mono">
              <a :href="'https://doi.org/' + p.doi" target="_blank" rel="noopener" :title="p.doi_raw">{{ p.doi }}</a>
              <a v-if="p.can_delete" href="#" class="lit-del" title="목록에서 삭제" @click.prevent="remove(p)">삭제</a>
            </td>
            <td><router-link :to="'/literature/' + p.id" class="lit-title">{{ p.title }}</router-link></td>
            <td>
              <template v-if="p.mine || (user && user.admin)">
                <select class="lit-inline-select" :value="p.group" :disabled="busyId === p.id" @change="changeGroup(p, $event.target.value)" title="주 분야 변경">
                  <option v-for="g in groups" :key="g.code" :value="g.code">{{ g.label }}</option>
                </select>
              </template>
              <template v-else>{{ p.group_label }}</template>
              <div class="lit-sub" v-if="p.groups && p.groups.length > 1">+ {{ subGroups(p).join(', ') }}</div>
            </td>
            <td>
              <select v-if="p.mine || (user && user.admin)" class="lit-inline-select" :value="p.status" :disabled="busyId === p.id" @change="changeStatus(p, $event.target.value)" title="상태 변경">
                <option v-for="s in data.by_status" :key="s.key" :value="s.key">{{ s.label }}</option>
              </select>
              <span v-else :class="'lit-status lit-status--' + p.status">{{ p.status_label }}</span>
            </td>
            <td>{{ p.uploader }}<span v-if="p.mine" class="lit-me">(나)</span></td>
            <td>{{ p.institution }}</td>
            <td class="mono">{{ fmtDate(p.created_at) }}</td>
            <td>
              <router-link v-if="p.extraction" :to="'/literature/' + p.id" :title="'v' + p.extraction.version + ' · ' + p.extraction.row_count + '행'">
                v{{ p.extraction.version }}<small> {{ p.extraction.row_count }}행</small>
              </router-link>
              <span v-else class="lit-dim">—</span>
            </td>
            <td>
              <router-link v-if="p.fulltext" :to="{ path: '/literature/' + p.id, query: { tab: 'md' } }"
                           :class="{ 'lit-dim': !p.fulltext.can_read }" :title="p.fulltext.can_read ? '원문 보기' : '열람 범위 밖'">
                v{{ p.fulltext.version }}
              </router-link>
              <span v-else class="lit-dim">—</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <div class="search_nodata" v-else-if="data && !loading">
        <h3>{{ hasFilter ? '조건에 맞는 논문이 없습니다.' : '아직 등록된 논문이 없습니다.' }}</h3>
        <p v-if="!hasFilter">문헌 업로드 토큰을 발급한 뒤 팀 패키지의 <code>lit_register.py</code> 로 DOI 를 등록하면 여기에 나타납니다.</p>
      </div>

      <div class="lit-paging" v-if="data && totalPages > 1">
        <button type="button" class="sm_btn" :disabled="page <= 1" @click="reload(page - 1)">이전</button>
        <span>{{ page }} / {{ totalPages }} 쪽 · 총 {{ data.total.toLocaleString() }}편</span>
        <button type="button" class="sm_btn" :disabled="page >= totalPages" @click="reload(page + 1)">다음</button>
      </div>

      <p class="lit-error" v-if="error">{{ error }}</p>
    </div>
    <Loading :visible="loading" />
  </div>
</template>

<script>
import Loading from "@/components/common/loading.vue";

const PAGE_SIZE = 50;

export default {
  name: "LitRegistry",
  components: { Loading },
  data() {
    return {
      loading: false,
      error: null,
      data: null,
      groups: [],
      search: "",
      filter: { q: "", group: "", institution: "", status: "", mine: false },
      page: 1,
      busyId: null,
      exporting: false,
    };
  },
  computed: {
    user() {
      const u = this.$store.state.user;
      return u && !u.error ? u : null;
    },
    hubKey() {
      return (process.env.BASE_URL || "/").indexOf("battery") >= 0 ? "battery" : "m3d";
    },
    pkgBase() {
      return (process.env.BASE_URL || "/") + "lit-pkg/" + this.hubKey + "/";
    },
    procedureFile() {
      return this.hubKey === "battery" ? "문헌데이터수집_세부연구팀_수행절차_KESH.md" : "문헌데이터수집_세부연구팀_수행절차_M3D.md";
    },
    apiFile() {
      return this.hubKey === "battery" ? "API_사용법_KESH.md" : "API_사용법_M3D.md";
    },
    totalPages() {
      return this.data ? Math.max(1, Math.ceil(this.data.total / PAGE_SIZE)) : 1;
    },
    hasFilter() {
      const f = this.filter;
      return !!(f.q || f.group || f.institution || f.status || f.mine);
    },
    aggregateTotal() {
      if (!this.data) return 0;
      return this.data.by_group.filter((g) => !g.aggregate_excluded).reduce((s, g) => s + g.count, 0);
    },
  },
  async mounted() {
    const q = this.$route.query || {};
    if (q.group) this.filter.group = String(q.group);
    if (q.status) this.filter.status = String(q.status);
    if (q.q) { this.filter.q = String(q.q); this.search = this.filter.q; }
    const groups = await this.$wApi.lit.groups();
    if (groups && !groups.error) this.groups = groups;
    await this.reload(1);
  },
  methods: {
    async reload(page) {
      this.loading = true;
      this.error = null;
      this.page = page || 1;
      const f = this.filter;
      const res = await this.$wApi.lit.papers({
        q: f.q || "", group: f.group || "", institution: f.institution || "", status: f.status || "",
        mine: f.mine ? "1" : "0", page: this.page, pageSize: PAGE_SIZE,
      });
      this.loading = false;
      if (!res || res.error) {
        this.error = (res && res.message) || "목록을 불러오지 못했습니다.";
        return;
      }
      this.data = res;
    },
    applySearch() {
      this.filter.q = this.search.trim();
      this.reload(1);
    },
    selectGroup(code) {
      this.filter.group = code;
      this.reload(1);
    },
    toggleStatus(key) {
      this.filter.status = this.filter.status === key ? "" : key;
      this.reload(1);
    },
    toggleInstitution(key) {
      this.filter.institution = this.filter.institution === key ? "" : key;
      this.reload(1);
    },
    pct(n) {
      return this.aggregateTotal ? Math.round((n / this.aggregateTotal) * 100) : 0;
    },
    subGroups(p) {
      return p.groups.filter((c) => c !== p.group).map((c) => {
        const g = this.groups.find((x) => x.code === c);
        return g ? g.label : c;
      });
    },
    fmtDate(iso) {
      return iso ? String(iso).slice(0, 10) : "";
    },
    async changeGroup(p, code) {
      if (!code || code === p.group) return;
      this.busyId = p.id;
      const res = await this.$wApi.lit.patchPaper({ group: code }, p.id);
      this.busyId = null;
      if (!res || res.error) { window.alert((res && res.message) || "분야를 바꾸지 못했습니다."); return; }
      await this.reload(this.page);
    },
    async changeStatus(p, status) {
      if (!status || status === p.status) return;
      this.busyId = p.id;
      const res = await this.$wApi.lit.patchPaper({ status }, p.id);
      this.busyId = null;
      if (!res || res.error) { window.alert((res && res.message) || "상태를 바꾸지 못했습니다."); return; }
      await this.reload(this.page);
    },
    async remove(p) {
      if (!window.confirm(`이 논문을 목록에서 삭제하시겠습니까?\n\n${p.doi}\n${p.title}\n\n삭제 후에는 같은 DOI 를 다시 등록할 수 있습니다.`)) return;
      this.busyId = p.id;
      const res = await this.$wApi.lit.deletePaper({}, p.id);
      this.busyId = null;
      if (res && res.error) { window.alert(res.message || "삭제하지 못했습니다."); return; }
      await this.reload(this.page);
    },
    async exportXlsx() {
      this.exporting = true;
      try {
        const f = this.filter;
        const rows = [];
        let page = 1;
        for (;;) {
          const res = await this.$wApi.lit.papers({
            q: f.q || "", group: f.group || "", institution: f.institution || "", status: f.status || "",
            mine: f.mine ? "1" : "0", page, pageSize: 500,
          });
          if (!res || res.error || !res.items.length) break;
          rows.push(...res.items);
          if (rows.length >= res.total || page >= 40) break;
          page += 1;
        }
        const ExcelJS = (await import("exceljs")).default || (await import("exceljs"));
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("문헌 수집");
        ws.columns = [
          { header: "DOI", key: "doi", width: 36 }, { header: "제목", key: "title", width: 80 },
          { header: "주 분야", key: "group", width: 10 }, { header: "부 분야", key: "groups", width: 14 },
          { header: "상태", key: "status", width: 10 }, { header: "올린 사람", key: "uploader", width: 12 },
          { header: "학교", key: "institution", width: 18 }, { header: "등록일", key: "created_at", width: 20 },
          { header: "추출 버전", key: "ext_v", width: 9 }, { header: "추출 행 수", key: "ext_rows", width: 9 }, { header: "원문 버전", key: "ft_v", width: 9 },
        ];
        rows.forEach((p) => ws.addRow({
          doi: p.doi, title: p.title, group: p.group, groups: (p.groups || []).filter((c) => c !== p.group).join(","),
          status: p.status_label, uploader: p.uploader, institution: p.institution, created_at: p.created_at,
          ext_v: p.extraction ? p.extraction.version : "", ext_rows: p.extraction ? p.extraction.row_count : "", ft_v: p.fulltext ? p.fulltext.version : "",
        }));
        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `literature_registry_${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      } catch (e) {
        window.alert("엑셀 내보내기에 실패했습니다: " + (e && e.message ? e.message : e));
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.lit-page { min-height: 60vh; }
.lit-intro { margin: 0 0 16px; color: #555; font-size: 14px; line-height: 1.6; }
.lit-intro a { color: #003f67; text-decoration: underline; }
.lit-pkg { display: block; margin-top: 4px; font-size: 13px; color: #556; }
.lit-chips { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 8px; }
.lit-chips--sub { margin-bottom: 16px; }
.lit-chip { border: 1px solid #cfd8e3; background: #fff; border-radius: 999px; padding: 6px 12px; font-size: 13px; color: #334; cursor: pointer; }
.lit-chip b { margin-left: 4px; color: #003f67; }
.lit-chip small { margin-left: 4px; color: #889; }
.lit-chip.on { background: #003f67; border-color: #003f67; color: #fff; }
.lit-chip.on b, .lit-chip.on small { color: #fff; }
.lit-chip.small { padding: 4px 10px; font-size: 12px; }
.lit-chip.muted { border-style: dashed; }
.lit-chip-label { font-size: 12px; color: #889; margin: 0 2px 0 6px; }
.lit-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-bottom: 12px; }
.lit-search { display: flex; gap: 6px; }
.lit-search input { width: 320px; height: 34px; border: 1px solid #cfd8e3; border-radius: 4px; padding: 0 10px; font-size: 13px; }
.lit-check { font-size: 13px; color: #445; display: flex; align-items: center; gap: 4px; }
.lit-toolbar-right { margin-left: auto; display: flex; gap: 6px; }
.lit-link-btn { text-decoration: none; }
.lit-table-wrap { overflow-x: auto; border-top: 2px solid #003f67; }
.lit-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 960px; }
.lit-table th { background: #f4f7fa; padding: 10px 8px; text-align: left; border-bottom: 1px solid #dde3ea; font-weight: 600; color: #223; white-space: nowrap; }
.lit-table td { padding: 9px 8px; border-bottom: 1px solid #eef1f5; vertical-align: top; color: #333; }
.lit-table tr:hover td { background: #fafcfe; }
.mono { font-family: Consolas, "Courier New", monospace; font-size: 12px; }
.lit-table a { color: #0c78b3; }
.lit-title { color: #223 !important; }
.lit-title:hover { text-decoration: underline; }
.lit-del { display: block; margin-top: 4px; font-size: 11px; color: #b23 !important; }
.lit-sub { font-size: 11px; color: #778; margin-top: 3px; }
.lit-inline-select { max-width: 100%; height: 26px; font-size: 12px; border: 1px solid #cfd8e3; border-radius: 4px; background: #fff; }
.lit-status { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; background: #eef1f5; }
.lit-status--candidate { background: #f2f2f2; color: #667; }
.lit-status--collecting { background: #e4f1fb; color: #0c5a8a; }
.lit-status--extracted { background: #e3f6ea; color: #1f7a3f; }
.lit-me { margin-left: 4px; font-size: 11px; color: #0c78b3; }
.lit-dim { color: #bbb; }
.lit-paging { display: flex; gap: 12px; align-items: center; justify-content: center; margin: 18px 0; font-size: 13px; color: #556; }
.lit-error { color: #b23; margin-top: 12px; }
.search_nodata p { color: #667; font-size: 13px; margin-top: 8px; }
.search_nodata code { background: #f4f7fa; padding: 1px 5px; border-radius: 3px; }
</style>
